/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.21.0
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { tableApi } from './store';
import type { ITableBasketItem, ITableButtonsMode, ITableInitialise } from './types';
import { getTableLoadSize } from './utils/getTableLoadSize';

export interface ITableSlice extends ITableInitialise {
    tableData: any[];
    tableDataCount: number;
    tableDataHasMore: boolean;
    tableLoadSize: number;
    tableSearchAfterValues?: string[] | null;
    /* Statuses */
    requestStatus: {
        isInitialised: boolean;
        meta: { isLoading: boolean, isError: boolean };
        list: { isLoading: boolean, isError: boolean };
        filterAndSort: { isLoading: boolean, isError: boolean };
    };
    /* Basket */
    basketAllIn?: boolean;
    fastBasketAllIn?: boolean;
    basketData?: ITableBasketItem[];
    basketCount?: number;
    basketHasMore?: boolean;
    basketParams?: any;
    /* Footer Buttons */
    buttonsMode?: ITableButtonsMode;
    // table id
    tableLocation?: string;
    // subscribers in roaming
    subscribersInRoamingCount?: number;
    subscribersInRoamingWithFilterCount?: number;
    filterSubscribersInRoaming?: boolean;
    context?: string;
    isFastActionPage?: boolean;
    isFastBasketPage?: boolean;
}

export const initialTableSliceState: ITableSlice = {
    // initial params
    meta: { url: '' },
    list: { url: '' },
    filterAndSort: { url: '' },
    // list
    tableData: [],
    tableDataCount: 0,
    tableDataHasMore: false,
    tableLoadSize: 0,
    // statuses
    requestStatus: {
        isInitialised: false,
        meta: { isLoading: false, isError: false },
        list: { isLoading: false, isError: false },
        filterAndSort: { isLoading: false, isError: false },
    },
};


export const tableSlice = createSlice({
    name: 'tableSlice',
    initialState: initialTableSliceState,
    reducers: {
        /* Для инициализации таблицы необходимо передать конфигурацию из url для tableApi  */
        initialise(state, action: PayloadAction<ITableInitialise>) {
            state.meta = action.payload.meta;
            state.list = action.payload.list;
            state.filterAndSort = action.payload.filterAndSort;
            state.basket = action.payload.basket;
            state.resize = action.payload.resize;
            state.columnSettings = action.payload.columnSettings;
            state.labels = action.payload.labels;
            // statuses
            if (action.payload.meta) {
                state.requestStatus.meta.isLoading = true;
                state.requestStatus.meta.isError = false;
            }
            state.requestStatus.isInitialised = true;
            // size
            state.tableLoadSize = getTableLoadSize(action.payload.list);
        },
        reset: () => initialTableSliceState,
        setTableLocationPathname(state, { payload }: PayloadAction<string>) {
            state.tableLocation = payload;
        },

        /* Сохранение списка (lazy-load) */
        setTableData(state, { payload }: PayloadAction<{ data: any[], from: number, count: number }>) {
            state.tableDataHasMore = payload.data.length === state.tableLoadSize;
            state.tableDataCount = payload.count;

            /* Если у нас всего одна запись, то для основных операций проставляем у неё чекбокс */
            if (payload.count === 1) {
                state.tableData = payload.data.map(item => ({ ...item, fastActionChecked: true }));
                state.fastBasketAllIn = true;
            } else {
                /* from = 0 - инициализация списка, далее запрос след страниц */
                state.tableData = payload.from === 0 ? payload.data : state.tableData.concat(payload.data);
            }
        },

        /* Сохранение параметра searchAfterValues для длинных списков, получаемых из elastic search */
        setTableSearchAfterValues(state, { payload }: PayloadAction<{ searchAfterValues: string[] | null }>) {
            /* searchAfterValues для последней страницы === null */
            state.tableSearchAfterValues = payload.searchAfterValues;
        },
        clearTableData(state) {
            /* from = 0 - инициализация списка, далее запрос след страниц */
            state.tableData = [];
            state.requestStatus.list.isLoading = true;
            state.requestStatus.list.isError = false;
        },

        /*
        *  Изменение кол-ва запрашиваемых данных для таблицы
        *  За изменение отвечает компонент TableDensity
        */
        setTableSize(state, { payload }: PayloadAction<{ size: number }>) {
            state.tableLoadSize = payload.size;
        },

        /* Изменение checkbox у таблицы */
        setCheckbox(state, { payload }: PayloadAction<{ id?: number, checked: boolean; }>) {
            /* Для check-all item не посылается */
            if (!payload.id) {
                state.tableData = state.tableData.map(row => ({ ...row, checked: payload.checked }));
            } else {
                const rowIndex = state.tableData.findIndex(row => row.id === payload.id);
                state.tableData[rowIndex] = { ...state.tableData[rowIndex], checked: payload.checked };
            }
        },

        /* Изменение checkbox у таблицы для быстрых действий, в обход общей корзины */
        setFastActionCheckbox(state, { payload }: PayloadAction<{ id?: number, checked: boolean; }>) {
            /* Для check-all item не посылается */
            if (!payload.id) {
                state.tableData = state.tableData.map(row => ({ ...row, fastActionChecked: payload.checked }));
                state.fastBasketAllIn = payload.checked;
            } else {
                const rowIndex = state.tableData.findIndex(row => row.id === payload.id);
                state.tableData[rowIndex] = { ...state.tableData[rowIndex], fastActionChecked: payload.checked };
                if (state.tableData.length === 1) {
                    state.fastBasketAllIn = payload.checked;
                }
            }
            if (
                state.tableDataCount === state.tableData.filter(item => item.fastActionChecked).length
            ) {
                state.fastBasketAllIn = true;
            } else {
                state.fastBasketAllIn = false;
            }
        },

        /* Флаг для check-all */
        setBasketAllIn(state, { payload }: PayloadAction<{ basketAllIn: boolean }>) {
            state.basketAllIn = payload.basketAllIn;
        },
        /* Сохранение списка выбранных элементов (lazy-load) */
        setBasket(
            state,
            { payload }: PayloadAction<{ data: ITableBasketItem[], from: number, count: number; params: any }>,
        ) {
            /* from = 0 - инициализация списка, далее запрос след страниц */
            state.basketData = payload.from === 0 ? payload.data : (state.basketData || []).concat(payload.data);
            state.basketHasMore = payload.data.length === 24;
            state.basketCount = payload.count;
            /*
             * @see setBasketParams
             */
            state.basketParams = payload.params;
        },
        /*
        * Дополнительные параметры для корзины (опционально).
        * В параметрах может приходить доп информации о состоянии кнопок таблицы в футере
        */
        setBasketParams(state, { payload }: PayloadAction<{ params: any }>) {
            state.basketParams = payload.params;
        },
        /* Изменение количества элементов в корзине */
        setBasketCount(state, { payload }: PayloadAction<{ count: number }>) {
            state.basketCount = payload.count;
        },
        uncheckItemsFromBasket(state, { payload }: PayloadAction<{ [itemId: string]: ITableBasketItem }>) {
            state.tableData = state.tableData.map(row => payload[row.id] ? ({ ...row, checked: false }) : row);
        },

        /*
        * Mode устанавливается для кнопок таблицы в футере и переопределяет их стандартное поведение
        */
        setButtonsMode(state, { payload }: PayloadAction<ITableButtonsMode>) {
            state.buttonsMode = payload;
        },

        /* Положение переключателя "Только в роуминге" */
        setFilterSubscribersInRoaming(
            state, 
            { payload }: PayloadAction<{filterSubscribersInRoaming: boolean | undefined }>,
        ) {
            state.filterSubscribersInRoaming = !!payload.filterSubscribersInRoaming;
        },
        /* Количество абонентов в роуминге: общее и видимое с учетом фильтров*/
        setSubscribersInRoamingCount(
            state, 
            { payload }: PayloadAction<{subscribersInRoamingCount: number | undefined}>,
        ) {
            state.subscribersInRoamingCount  = payload.subscribersInRoamingCount ?? 0;
        },
        setSubscribersInRoamingWithFilterCount(
            state, 
            { payload }: PayloadAction<{subscribersInRoamingWithFilterCount: number | undefined}>,
        ) {
            state.subscribersInRoamingWithFilterCount = payload.subscribersInRoamingWithFilterCount ?? 0;
        },

        /* For the fast action pages */
        setTableContext(
            state, 
            { payload }: PayloadAction<{context: string | undefined}>,
        ) {
            state.context = payload.context;
        },
        setIsFastActionPage(
            state,
            { payload }: PayloadAction<{isFastActionPage: boolean | undefined}>,
        ) {
            state.isFastActionPage = payload.isFastActionPage;
        },
        setIsFastBasketPage(
            state,
            { payload }: PayloadAction<{isFastBasketPage: boolean | undefined}>,
        ) {
            state.isFastBasketPage = payload.isFastBasketPage;
        },
    },
    extraReducers: (builder) => {

        /* Loading */
        builder.addMatcher(tableApi.endpoints.getTableMeta.matchPending, (state) => {
            state.requestStatus.meta.isLoading = true;
            state.requestStatus.meta.isError = false;
        });
        builder.addMatcher(tableApi.endpoints.getTableList.matchPending, (state) => {
            state.requestStatus.list.isLoading = true;
            state.requestStatus.list.isError = false;
        });
        builder.addMatcher(tableApi.endpoints.setTableFilterAndSort.matchPending, (state) => {
            state.requestStatus.filterAndSort.isLoading = true;
            state.requestStatus.filterAndSort.isError = false;
        });
        /* Success */
        builder.addMatcher(tableApi.endpoints.getTableMeta.matchFulfilled, (state) => {
            state.requestStatus.meta.isLoading = false;
            state.requestStatus.meta.isError = false;
        });
        builder.addMatcher(tableApi.endpoints.getTableList.matchFulfilled, (state) => {
            state.requestStatus.list.isLoading = false;
            state.requestStatus.list.isError = false;
        });
        builder.addMatcher(tableApi.endpoints.setTableFilterAndSort.matchFulfilled, (state) => {
            state.requestStatus.filterAndSort.isLoading = false;
            state.requestStatus.filterAndSort.isError = false;
        });
        /* Failure */
        builder.addMatcher(tableApi.endpoints.getTableMeta.matchRejected, (state) => {
            state.requestStatus.meta.isLoading = false;
            state.requestStatus.meta.isError = true;
        });
        builder.addMatcher(tableApi.endpoints.getTableList.matchRejected, (state, action) => {
            if (action.payload) {
                state.requestStatus.list.isLoading = false;
                state.requestStatus.list.isError = true;
                state.tableDataHasMore = false;
            }
        });
        builder.addMatcher(tableApi.endpoints.setTableFilterAndSort.matchRejected, (state) => {
            state.requestStatus.filterAndSort.isLoading = false;
            state.requestStatus.filterAndSort.isError = true;
        });
    },
});

export const {
    clearTableData,
    initialise,
    reset,
    setBasket,
    setBasketAllIn,
    setBasketCount,
    setBasketParams,
    setButtonsMode,
    setCheckbox,
    setFastActionCheckbox,
    setTableContext,
    setIsFastActionPage,
    setIsFastBasketPage,
    setTableData,
    setTableLocationPathname,
    setTableSearchAfterValues,
    setTableSize,
    uncheckItemsFromBasket,
    setFilterSubscribersInRoaming,
    setSubscribersInRoamingCount,
    setSubscribersInRoamingWithFilterCount,
} = tableSlice.actions;

